import { create } from 'zustand'

interface User {
  id: string
  email: string
  name: string
  language?: string
}

interface Organization {
  id: string
  name: string
  role: string
}

interface CreateOrganizationInput {
  name: string
  businessId?: string
  type?: string
  vatRegistered?: boolean
  fiscalYearStart?: string
}

interface AuthState {
  user: User | null
  organizations: Organization[]
  activeOrg: Organization | null
  isLoading: boolean
  setUser: (user: User) => void
  setOrganizations: (orgs: Organization[]) => void
  setActiveOrg: (org: Organization) => void
  logout: () => Promise<void>
  setLoading: (loading: boolean) => void
  createOrganization: (input: CreateOrganizationInput) => Promise<Organization>
  deleteOrganization: (orgId: string) => Promise<void>
  refreshOrganizations: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organizations: [],
  activeOrg: null,
  isLoading: true,
  setUser: (user) => set({ user }),
  setOrganizations: (orgs) => set({ organizations: orgs }),
  setActiveOrg: (org) => {
    try { localStorage.setItem('activeOrgId', org.id) } catch {}
    set({ activeOrg: org })
  },
  logout: async () => {
    // Cookie is cleared by backend, just reset state
    try { localStorage.removeItem('activeOrgId') } catch {}
    set({ user: null, organizations: [], activeOrg: null })
  },
  setLoading: (loading) => set({ isLoading: loading }),
  createOrganization: async (input: CreateOrganizationInput) => {
    const response = await apiFetch(`${API_BASE}/organizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || 'Failed to create organization')
    }

    const data = await response.json()
    const newOrg: Organization = {
      id: data.organization.id,
      name: data.organization.name,
      role: 'owner',
    }

    // Add to organizations list and set as active
    const currentOrgs = get().organizations
    set({
      organizations: [...currentOrgs, newOrg],
      activeOrg: newOrg,
    })

    return newOrg
  },
  deleteOrganization: async (orgId: string) => {
    const response = await apiFetch(`${API_BASE}/organizations/${orgId}`, { method: 'DELETE' })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || 'Failed to delete organization')
    }
    const currentOrgs = get().organizations.filter(o => o.id !== orgId)
    const currentActive = get().activeOrg
    set({
      organizations: currentOrgs,
      activeOrg: currentActive?.id === orgId ? (currentOrgs[0] || null) : currentActive,
    })
  },
  refreshOrganizations: async () => {
    const response = await apiFetch(`${API_BASE}/organizations`)
    if (response.ok) {
      const data = await response.json()
      const orgs: Organization[] = (data.organizations || []).map((o: { id: string; name: string; role?: string }) => ({
        id: o.id,
        name: o.name,
        role: o.role || 'member',
      }))
      set({ organizations: orgs })
      
      // If current active org is not in the list, set first as active
      const currentActive = get().activeOrg
      if (!currentActive || !orgs.find((o) => o.id === currentActive.id)) {
        set({ activeOrg: orgs[0] || null })
      }
    }
  },
}))

// API base URL - fallback to production backend if env var not set
export const API_BASE = import.meta.env.VITE_API_URL || 'https://kirjuri-backend.tuomas-508.workers.dev'

// Debug log for production builds
if (import.meta.env.PROD) {
  console.log('[Auth] API_BASE configured:', API_BASE)
}

// Helper to get auth headers - SECURITY: No localStorage to prevent XSS
export const getAuthHeaders = () => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  // SECURITY: HttpOnly cookie is used for auth, no localStorage (XSS prevention)
  // Browser automatically sends HttpOnly cookies with credentials: 'include'
  return headers
}

// Fetch wrapper that includes credentials for HttpOnly cookie-based auth
export const apiFetch = (url: string, options: RequestInit = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include', // Important: sends HttpOnly cookies (XSS-safe)
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  })
}
